import { useCallback, useRef, type VoidFunctionComponent } from 'react';

import { useOnClickOutside, useOnEscPress, useResettableScroll } from '../../../../../hooks';
import usePaginatedReleases from '../../../../shared/releaseGroups/hooks/usePaginatedReleases';

import Body from './ReleaseSelectorBody';
import Footer from './ReleaseSelectorFooter';
import Header from './ReleaseSelectorHeader';

import styles from './ReleaseSelector.module.less';

type Props = {
  projectGroupId: number;
  onClose: () => void;
  onReleaseSelect: (releaseId: number) => void;
  selectedReleaseId?: number;
};

/**
 * ReleaseGroupsReleaseSelector is a dropdown component which allows users to
 * search and select a release to set as the "active" release.
 */
const ReleaseGroupsReleaseSelector: VoidFunctionComponent<Props> = ({
  projectGroupId,
  onClose,
  onReleaseSelect,
  selectedReleaseId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { ref: innerRef, onResetScroll } = useResettableScroll<HTMLDivElement>();
  const {
    isLoading,
    releases,
    total,
    page,
    count,
    setPage,
    search,
    onSearchChange,
  } = usePaginatedReleases(projectGroupId as unknown as string); // TODO: Fix this typing issue

  //  Set up a listener to close the dropdown when the user clicks outside of it.
  useOnClickOutside({ ref: containerRef, handler: onClose });
  //  Set up a listener to close the dropdown when the user presses the escape key.
  useOnEscPress(onClose);

  const hasNoResults = !isLoading && !releases.length;

  const onSearchChangeAndResetScroll = useCallback((value: string) => {
    onSearchChange(value);
    setPage(1); // Reset the page to 1 when the search changes
    onResetScroll();
  }, [onResetScroll, onSearchChange, setPage]);

  const onSetPageChangeAndResetScroll = useCallback((nextPage: number) => {
    setPage(nextPage);
    onResetScroll();
  }, [onResetScroll, setPage]);

  return (
    <div ref={containerRef} className={styles.container}>
      <Header search={search} onChange={onSearchChangeAndResetScroll} />
      <div ref={innerRef} className={styles.inner}>
        <Body
          hasNoResults={hasNoResults}
          isLoading={isLoading}
          count={count}
          releases={releases}
          selectedReleaseId={selectedReleaseId}
          onReleaseSelect={onReleaseSelect}
        />
      </div>
      {!hasNoResults && (
        <Footer
          projectGroupId={projectGroupId}
          page={page}
          count={count}
          total={total}
          setPage={onSetPageChangeAndResetScroll}
        />
      )}
    </div>
  );
};

export default ReleaseGroupsReleaseSelector;
